import { nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import useMediaStore from '../../stores/media';
import useSmilStore from '../../stores/smil';
import usePlaylistStore, { type PlaylistItem } from '../../stores/playlist';
import { Manifest } from '../../util/manifest';
import { EpubJsSpineItem } from '../../types/epubjs';
import { getTracksForLanguage } from '../../util/track-utils';
import { getElementFromEpubCFI } from '../../util/location';

const CHUNK_SIZE = 15;

const useAudioOnlyReader = () => {
  const { tracksInPage } = useSmilStore();
  const mediaStore = useMediaStore();
  const { preferredLanguage, mainLanguage, requestedPlayerPosition } = storeToRefs(mediaStore);
  const { setCurrentTrack, addTracks, clearTracks, setIsLastTrack } = mediaStore;
  const playlistStore = usePlaylistStore();
  const { resolutionProgress, isInitializing, playlist, currentPlaylistIndex } = storeToRefs(playlistStore);
  const {
    goToPlaylistIndex: storeGoToPlaylistIndex,
    initializePlaylist,
    goToTime,
    goToTocHref: storeGoToTocHref,
    getPlaylistIndexForTocHref,
  } = playlistStore;

  /**
   * Build a playlist from spine items that have audio (SMIL overlay)
   * Processes items in chunks to prevent UI blocking
   */
  const buildPlaylist = async (spineItems: { [id: string]: EpubJsSpineItem }): Promise<PlaylistItem[]> => {
    const items: PlaylistItem[] = [];

    // Filter spine items with overlays for progress tracking
    const spineItemsWithOverlay = Object.entries(spineItems).filter(([, item]) => item.overlay);
    const totalItems = spineItemsWithOverlay.length;
    let processedItems = 0;

    // Process items in chunks to prevent UI blocking
    for (let i = 0; i < spineItemsWithOverlay.length; i += CHUNK_SIZE) {
      const chunk = spineItemsWithOverlay.slice(i, i + CHUNK_SIZE);

      const chunkResults = await Promise.all(
        chunk.map(async ([id, spineItem]) => {
          try {
            let tracks = await tracksInPage(spineItem);

            // Apply language preference if needed
            if (preferredLanguage.value && mainLanguage.value && preferredLanguage.value !== mainLanguage.value) {
              tracks = getTracksForLanguage(tracks, preferredLanguage.value);
            }

            if (tracks.length > 0) {
              return {
                spineIndex: spineItem.index,
                spineId: id,
                href: spineItem.path,
                tracks,
              };
            }
          } catch (error) {
            console.error(`Failed to load tracks for spine item ${id}:`, error);
          }
          return null;
        }),
      );

      // Add non-null results to items
      items.push(...chunkResults.filter((item): item is PlaylistItem => item !== null));

      // Update progress (50% of total progress allocated to track loading)
      processedItems += chunk.length;
      resolutionProgress.value = Math.round((processedItems / totalItems) * 50);

      // Yield to UI between chunks
      await nextTick();
    }

    // Sort by spine index to ensure sequential order
    return items.sort((a, b) => a.spineIndex - b.spineIndex);
  };

  /**
   * Load tracks for a specific playlist item into the media store
   * For audio-only books, we directly set tracks without spine-based indexing
   */
  const loadPlaylistItem = (item: PlaylistItem, trackIndex?: number) => {
    const flaggedTracks = item.tracks.map((track) => ({
      ...track,
      textMarksAdded: true, // Flag as added since we don't have DOM
    }));

    // For audio-only books, directly set currentPageTracks without using sparse spine array
    // This avoids the undefined entries that occur with non-sequential spine indices
    clearTracks();
    addTracks(flaggedTracks, 0); // Use index 0 to avoid sparse array issues

    if (flaggedTracks.length === 0) return;

    // Set specific track as current if given a track index
    if (trackIndex && trackIndex > 0 && trackIndex < flaggedTracks.length) {
      setCurrentTrack(flaggedTracks[trackIndex]);
      return;
    }

    // Set first track as current
    setCurrentTrack(flaggedTracks[0]);
  };

  /**
   * Navigate to the next playlist item
   */
  const goToNextPlaylistItem = async () => {
    if (currentPlaylistIndex.value < playlist.value.length - 1) {
      const nextIndex = currentPlaylistIndex.value + 1;
      storeGoToPlaylistIndex(nextIndex);
      loadPlaylistItem(playlist.value[nextIndex]);
      return true;
    } else {
      // End of playlist
      setIsLastTrack(true);
      return false;
    }
  };

  /**
   * Navigate to previous playlist item
   */
  const goToPreviousPlaylistItem = async () => {
    if (currentPlaylistIndex.value > 0) {
      const prevIndex = currentPlaylistIndex.value - 1;
      storeGoToPlaylistIndex(prevIndex);
      loadPlaylistItem(playlist.value[prevIndex]);
      return true;
    }
    return false;
  };

  /**
   * Jump to a specific playlist index
   */
  const goToPlaylistIndex = (index: number, trackIndex?: number, positionInTrack?: number) => {
    if (index >= 0 && index < playlist.value.length) {
      storeGoToPlaylistIndex(index); // Update store
      loadPlaylistItem(playlist.value[index]);

      // If specific track and position provided, set them in media store
      if (trackIndex !== undefined && playlist.value[index].tracks[trackIndex]) {
        const targetTrack = playlist.value[index].tracks[trackIndex];
        setCurrentTrack(targetTrack);

        if (positionInTrack !== undefined) {
          requestedPlayerPosition.value = positionInTrack;
        }
      }

      return true;
    }
    return false;
  };

  /**
   * Find playlist index by spine index
   */
  const findPlaylistIndexBySpine = (spineIndex: number): number => {
    return playlist.value.findIndex((item) => item.spineIndex === spineIndex);
  };

  /**
   * Navigate to a spine item at a specific audio position when cfi is provided.
   * Uses srcFragmentId extracted from the CFI to locate the exact track within the playlist item.
   */
  const goToLocation = (spineIndex: number, cfi?: string) => {
    const playlistIndex = findPlaylistIndexBySpine(spineIndex);
    if (playlistIndex < 0) return false;

    if (!cfi) {
      return goToPlaylistIndex(playlistIndex);
    }

    const tracks = playlist.value[playlistIndex].tracks;
    const fragmentId = getElementFromEpubCFI(cfi);

    let trackIndex = 0;
    if (fragmentId) {
      const idx = tracks.findIndex((t) => t.text.srcFragmentId === fragmentId);
      if (idx >= 0) trackIndex = idx;
    }

    return goToPlaylistIndex(playlistIndex, trackIndex);
  };

  const goToTocHref = (href: string) => {
    const updatedStore = storeGoToTocHref(href); // Update store
    const playlistIndex = getPlaylistIndexForTocHref(href);

    if (updatedStore && playlistIndex && playlistIndex >= 0 && playlistIndex < playlist.value.length) {
      return goToPlaylistIndex(playlistIndex);
    }
    return false;
  };

  /**
   * Get current playlist information
   */
  const getPlaylistInfo = () => {
    return {
      currentIndex: currentPlaylistIndex.value,
      totalItems: playlist.value.length,
      currentItem: playlist.value[currentPlaylistIndex.value],
    };
  };

  /**
   * Syncs the playlist with a newly selected audio language, by rebuilding the entire playlist based for the new
   * preferred language.
   * @param manifestData - The manifest containing spine items, SMIL items etc.
   * @param targetTrackIndex - The index of the track within the current spine item to resume from.
   */
  const switchAudioLanguage = async (manifestData: Manifest, targetTrackIndex: number) => {
    console.debug('Switching the audio language');
    const builtPlaylist = await buildPlaylist(manifestData.spineItems);

    if (builtPlaylist.length === 0) {
      console.warn('No audio content found in book');
      isInitializing.value = false;
      return;
    }

    // Store current position before re-initializing playlist
    const targetPlaylistIndex = currentPlaylistIndex.value;

    initializePlaylist(builtPlaylist);

    // Restore the old position
    if (targetPlaylistIndex >= 0 && targetPlaylistIndex < playlist.value.length) {
      const targetPlaylist = playlist.value[targetPlaylistIndex];

      if (targetTrackIndex >= 0 && targetTrackIndex < targetPlaylist.tracks.length) {
        let targetTrackStartTime = targetPlaylist.startTime;

        // Add duration of all tracks before the target track
        for (let i = 0; i < targetTrackIndex; i++) {
          const track = targetPlaylist.tracks[targetTrackIndex];
          const trackDuration = (track.audio.clipEnd ?? 0) - (track.audio.clipBegin ?? 0);
          targetTrackStartTime += trackDuration;
        }

        // Navigate to the calculated time
        goToTime(targetTrackStartTime);
        //Load targeted playlist item
        loadPlaylistItem(playlist.value[targetPlaylistIndex], targetTrackIndex);
        return;
      }
    }

    // Fallback: Load the first playlist item
    loadPlaylistItem(playlist.value[0], 0);
  };

  /**
   * Setup audio-only mode
   */
  const setup = async (manifestData: Manifest) => {
    console.debug('Setting up audio-only mode');

    // Start initialization
    isInitializing.value = true;
    resolutionProgress.value = 0;

    // Build a playlist from spine items with audio (takes 0-50% of progress)
    const builtPlaylist = await buildPlaylist(manifestData.spineItems);

    console.debug(`Audio-only playlist created with ${builtPlaylist.length} items`);

    if (builtPlaylist.length === 0) {
      console.warn('No audio content found in book');
      isInitializing.value = false;
      return;
    }

    // Initialize the playlist store (takes 50-100% of progress)
    // Note: NavigationStructureStore should already be initialized with ToC and Pages
    initializePlaylist(builtPlaylist);

    // Load the first playlist item (now available from store)
    loadPlaylistItem(playlist.value[0]);
  };

  return {
    setup,
    buildPlaylist,
    goToNextPlaylistItem,
    goToPreviousPlaylistItem,
    goToPlaylistIndex,
    goToLocation,
    goToTocHref,
    getPlaylistInfo,
    switchAudioLanguage,
    getCurrentPlaylistIndex: () => currentPlaylistIndex,
  };
};

export default useAudioOnlyReader;
